/**
 * N2 · Competência 4 (Grandezas e medidas) · task-model H1.24.K01
 *
 *   "Calcular a distância real entre dois lugares, medindo num mapa com escala."
 *
 * Um mapa com algumas cidades e, por cima, uma RÉGUA virtual que o aluno arrasta e gira
 * (ver `ui/regua.ts`). Ele mede, no mapa, a distância entre as duas cidades pedidas e
 * a converte pela escala. A régua é ferramenta — o item é N2 — e a resposta é digitada.
 *
 * Duas escalas:
 *  - GRÁFICA: uma barra de 2 ou 3 cm com o total escrito na ponta ("0 — 60 km"). O erro
 *    que dá nome à variante é usar o total da barra para cada centímetro.
 *  - NUMÉRICA: "Escala 1 : 2.000.000". Aqui o que pesa é a conversão de centímetros
 *    para quilômetros (÷ 100.000), e errá-la por um fator de 100 ou de 1.000 tem nome.
 *
 * Nas duas: responder a própria medida do mapa, e medir a partir do 1 da régua.
 *
 * As cidades ficam em posições cuja distância é um número exato de meios centímetros
 * (ternos pitagóricos, girados e refletidos): medir é ler a régua, não arredondar.
 */

import { formatBR } from '../../core/num.ts';
import type { Rng } from '../../core/rng.ts';
import { sampleWhere } from '../../core/sampling.ts';
import type { Vec2 } from '../../geom/vec.ts';
import { scene, type Shape } from '../../render/scene.ts';
import type { DifficultySourceCode } from '../../schema/difficulty.ts';
import type { ErrorCode } from '../../schema/errors.ts';
import {
  ESC_BARRA_INTEIRA,
  ESC_CONVERSAO,
  ESC_MEDIDA_MAPA,
  ESC_REGUA_NO_UM,
} from '../../schema/errors.ts';
import type { ItemTemplate, NumericEntryInteraction } from '../../schema/item.ts';
import { templateId } from '../../schema/item.ts';
import { br, strong, txt, type RichText } from '../../schema/richtext.ts';
import { skillCode, taskModelCode } from '../../schema/taxonomy.ts';

/** Unidades de desenho por centímetro do mapa. */
export const CM = 30;
const LARG = 12;
const ALT = 8;
const REGUA = 11;

const CIDADES = ['Aurora', 'Bonito', 'Cedral', 'Diamante', 'Estrela', 'Formosa', 'Guará'] as const;

/** Deslocamentos, em cm, com distância exata em meios centímetros. */
const DESLOCAMENTOS: readonly (readonly [number, number])[] = [
  [3, 4],
  [6, 8],
  [4.5, 6],
  [2.5, 6],
  [4, 7.5],
  [4, 0],
  [5, 0],
  [6, 0],
  [7, 0],
  [8, 0],
  [9, 0],
  [6.5, 0],
  [7.5, 0],
];

type Tipo = 'grafica' | 'numerica';

export interface Params {
  readonly tipo: Tipo;
  readonly kmPorCm: number;
  /** Comprimento da barra da escala gráfica, em cm. */
  readonly barraCm: number;
  /** As duas cidades da pergunta e as outras, em cm. */
  readonly P: Vec2;
  readonly Q: Vec2;
  readonly outras: readonly Vec2[];
  readonly nomes: readonly string[];
}

const dentro = (c: Vec2): boolean => c.x >= 1 && c.x <= LARG - 1 && c.y >= 1 && c.y <= ALT - 1;
/** O canto da escala (embaixo, à esquerda) e o da rosa dos ventos (em cima, à direita) ficam livres. */
const livre = (c: Vec2): boolean => !(c.x < 4.6 && c.y < 2.2) && !(c.x > 10.2 && c.y > 5.6);

function drawParams(rng: Rng): Params {
  const tipo: Tipo = rng.bool() ? 'grafica' : 'numerica';
  const kmPorCm = tipo === 'grafica' ? rng.pick([10, 20, 25, 30]) : rng.pick([10, 20, 50]);
  const barraCm = rng.pick([2, 3]);
  const geo = sampleWhere(
    rng.fork('mapa'),
    (g) => {
      const [a, b] = g.pick(DESLOCAMENTOS);
      const troca = g.bool();
      const dx = (troca ? b : a) * (g.bool() ? 1 : -1);
      const dy = (troca ? a : b) * (g.bool() ? 1 : -1);
      const P = { x: g.int(2, 2 * LARG - 2) / 2, y: g.int(2, 2 * ALT - 2) / 2 };
      const Q = { x: P.x + dx, y: P.y + dy };
      const d = Math.hypot(dx, dy);
      if (d < 4 || d > 10) return null;
      // Quilômetros inteiros: com 25 km por cm, só medidas inteiras.
      if (!Number.isInteger(d * kmPorCm)) return null;
      if (![P, Q].every((c) => dentro(c) && livre(c))) return null;
      const outras: Vec2[] = [];
      for (let t = 0; t < 60 && outras.length < 3; t++) {
        const c = { x: g.int(2, 2 * LARG - 2) / 2, y: g.int(2, 2 * ALT - 2) / 2 };
        if (livre(c) && [P, Q, ...outras].every((o) => Math.hypot(o.x - c.x, o.y - c.y) >= 2.2))
          outras.push(c);
      }
      return outras.length >= 2 ? { P, Q, outras } : null;
    },
    (x) => x !== null,
    { label: 'mapa', maxTries: 400 },
  ) as { P: Vec2; Q: Vec2; outras: Vec2[] };
  const nomes = rng
    .fork('nomes')
    .shuffle([...CIDADES])
    .slice(0, 2 + geo.outras.length);
  return { tipo, kmPorCm, barraCm, ...geo, nomes };
}

const num = (v: number): string => formatBR(v, Number.isInteger(v) ? 0 : 1);
const km = (v: number): string => `${formatBR(v, 0)} km`;

/** O contorno da região: decoração, com tinta apagada. */
const REGIAO: readonly Vec2[] = [
  { x: 0.4, y: 2.6 },
  { x: 1.2, y: 6.4 },
  { x: 3.1, y: 7.6 },
  { x: 6.2, y: 7.3 },
  { x: 8.8, y: 7.7 },
  { x: 11.6, y: 6.9 },
  { x: 11.4, y: 3.8 },
  { x: 11.7, y: 1.1 },
  { x: 8.4, y: 0.4 },
  { x: 5.1, y: 0.7 },
  { x: 2.2, y: 0.3 },
];

function mapa(p: Params): Shape[] {
  const t = (c: Vec2): Vec2 => ({ x: c.x * CM, y: c.y * CM });
  const shapes: Shape[] = [
    {
      t: 'rect',
      at: { x: 0, y: 0, w: LARG * CM, h: ALT * CM },
      role: 'auxiliary',
      fill: 'surface',
    },
    { t: 'poly', pts: REGIAO.map(t), closed: true, role: 'muted', fill: 'muted-fill' },
    // Rosa dos ventos: só o norte.
    { t: 'seg', a: t({ x: 11.2, y: 6.2 }), b: t({ x: 11.2, y: 7.3 }), role: 'given', arrow: 'end' },
    { t: 'label', at: t({ x: 11.2, y: 7.3 }), text: [txt('N')], anchor: 'w' },
  ];
  const cidades = [p.P, p.Q, ...p.outras];
  cidades.forEach((c, i) =>
    shapes.push({ t: 'point', p: t(c), role: 'given', name: p.nomes[i] as string }),
  );

  const x0 = 0.6;
  const y0 = 0.7;
  if (p.tipo === 'grafica') {
    for (let i = 0; i < p.barraCm; i++) {
      shapes.push({
        t: 'rect',
        at: { x: (x0 + i) * CM, y: y0 * CM, w: CM, h: 0.2 * CM },
        role: 'given',
        fill: i % 2 === 0 ? 'given-fill' : 'surface',
      });
    }
    shapes.push({ t: 'label', at: t({ x: x0, y: y0 + 0.2 }), text: [txt('0')], anchor: 'n' });
    shapes.push({
      t: 'label',
      at: t({ x: x0 + p.barraCm, y: y0 + 0.2 }),
      text: [txt(km(p.barraCm * p.kmPorCm))],
      anchor: 'n',
    });
  } else {
    shapes.push({
      t: 'label',
      at: t({ x: x0 - 0.1, y: y0 + 0.1 }),
      text: [txt(`Escala 1 : ${formatBR(p.kmPorCm * 100000, 0)}`)],
      anchor: 'e',
    });
  }
  return shapes;
}

type Erro = NumericEntryInteraction['errorValues'][number];

function errosDe(p: Params, d: number, chave: number): Erro[] {
  const s = p.kmPorCm;
  const out: { value: number; error: ErrorCode; explanation: RichText }[] = [
    {
      value: d,
      error: ESC_MEDIDA_MAPA,
      explanation: [
        txt(`${num(d)} é a distância no mapa, em centímetros. `),
        txt(`Cada centímetro do mapa vale ${km(s)} na realidade: ${num(d)} × ${s} = ${km(chave)}.`),
      ],
    },
    {
      value: (d + 1) * s,
      error: ESC_REGUA_NO_UM,
      explanation: [
        txt(
          `Essa resposta corresponde a ${num(d + 1)} cm no mapa, 1 cm a mais que a medida certa, ${num(d)} cm — `,
        ),
        txt('como quem põe a cidade de partida na marca do 1 da régua, e não na do 0.'),
      ],
    },
  ];
  if (p.tipo === 'grafica') {
    const total = p.barraCm * s;
    out.push({
      value: d * total,
      error: ESC_BARRA_INTEIRA,
      explanation: [
        txt(
          `Você usou ${km(total)} para cada centímetro: ${num(d)} × ${total} = ${km(d * total)}. `,
        ),
        txt(
          `Mas a barra de escala tem ${p.barraCm} cm, e os ${km(total)} valem para ela inteira: cada centímetro vale ` +
            `${total} ÷ ${p.barraCm} = ${km(s)}. Logo, ${num(d)} × ${s} = ${km(chave)}.`,
        ),
      ],
    });
  } else {
    const N = s * 100000;
    const cmReais = d * N;
    const certo = `${formatBR(cmReais, 0)} ÷ 100.000 = ${km(chave)}`;
    out.push(
      {
        value: cmReais / 1000,
        error: ESC_CONVERSAO,
        explanation: [
          txt(
            `No real, são ${num(d)} × ${formatBR(N, 0)} = ${formatBR(cmReais, 0)} cm. Você dividiu por 1.000, `,
          ),
          txt(`mas 1 km tem 100.000 cm (1.000 m de 100 cm cada): ${certo}.`),
        ],
      },
      {
        value: cmReais / 100,
        error: ESC_CONVERSAO,
        explanation: [
          txt(
            `${formatBR(cmReais / 100, 0)} é a distância em METROS: ${formatBR(cmReais, 0)} cm ÷ 100. `,
          ),
          txt(`Para chegar a quilômetros, falta dividir por 1.000: ${certo}.`),
        ],
      },
    );
  }
  return out.filter((e) => Math.abs(e.value - chave) > s * 0.5);
}

export const escalaMapa: ItemTemplate<Params, number> = {
  id: templateId('h1-24-K01-escala-mapa'),
  version: 1,
  taskModel: taskModelCode('H1.24.K01'),
  skills: [skillCode('H1.24'), skillCode('H1.21')],
  axis: 'II',
  level: 'N2',
  title: 'Distância real num mapa com escala',
  responseModel: '2PL',
  difficultySources: ['steps.2'],
  params: drawParams,
  solve: (p) => Math.hypot(p.Q.x - p.P.x, p.Q.y - p.P.y) * p.kmPorCm,
  buggy: [],
  variantDifficulty(p) {
    const fontes: DifficultySourceCode[] = [];
    if (p.tipo === 'numerica') fontes.push('steps.3plus');
    if (p.P.x !== p.Q.x && p.P.y !== p.Q.y) fontes.push('select-information');
    return fontes;
  },
  build(p, chave) {
    const d = Math.hypot(p.Q.x - p.P.x, p.Q.y - p.P.y);
    const [nP, nQ] = p.nomes as readonly [string, string];
    const escalaTxt =
      p.tipo === 'grafica'
        ? `uma escala gráfica: uma barra de ${p.barraCm} cm, marcada de 0 a ${km(p.barraCm * p.kmPorCm)}`
        : `a escala 1 : ${formatBR(p.kmPorCm * 100000, 0)}`;
    const alt =
      `Mapa de uma região com as cidades ${p.nomes.join(', ')}, a indicação do norte e ${escalaTxt}, no canto inferior ` +
      `esquerdo. Pelo desenho, a distância entre ${nP} e ${nQ} no mapa é de ${num(d)} cm. Uma régua de ${REGUA} cm, ` +
      'graduada em milímetros, pode ser arrastada e girada sobre o mapa.';
    return {
      stem: [
        txt(
          p.tipo === 'grafica'
            ? 'O mapa abaixo mostra algumas cidades de uma região. A barra no canto do mapa é a escala gráfica.'
            : 'O mapa abaixo mostra algumas cidades de uma região e foi desenhado na escala indicada no canto.',
        ),
      ],
      comando: [
        txt(`Qual é a distância real, em linha reta, entre `),
        strong(nP),
        txt(' e '),
        strong(nQ),
        txt(', em quilômetros?'),
      ],
      stimuli: [
        {
          kind: 'measurable',
          motion: 'interactive',
          audio: { need: 'none' },
          id: 'mapa',
          alt,
          scene: scene(mapa(p), {
            // À direita, folga para a bolinha de girar a régua.
            viewBox: { x: -0.6 * CM, y: -2.1 * CM, w: (LARG + 1.7) * CM, h: (ALT + 2.7) * CM },
            a11y: { title: 'Mapa com escala', desc: alt },
          }),
          regua: {
            comprimento: REGUA,
            escala: CM,
            subdivisoes: 10,
            unidade: 'cm',
            inicio: { x: 0.6 * CM, y: -0.35 * CM },
            angulo: 0,
          },
        },
      ],
      interaction: {
        kind: 'numeric-entry',
        unit: 'km',
        decimals: 0,
        keyValue: chave,
        // 1,5 mm de folga na leitura da régua.
        absTol: p.kmPorCm * 0.15,
        errorValues: errosDe(p, d, chave),
      },
      dicas: [
        [
          txt(
            'Ponha o 0 da régua sobre a primeira cidade, gire a régua até ela passar pela segunda e leia a medida.',
          ),
        ],
        [
          txt(
            p.tipo === 'grafica'
              ? `A barra de escala tem ${p.barraCm} cm e, inteira, vale ${km(p.barraCm * p.kmPorCm)}. Quanto vale 1 cm?`
              : `Na escala 1 : ${formatBR(p.kmPorCm * 100000, 0)}, 1 cm do mapa são ${formatBR(p.kmPorCm * 100000, 0)} cm ` +
                  'na realidade. Quantos quilômetros é isso? (1 km = 100.000 cm)',
          ),
        ],
        [txt('Multiplique a medida do mapa, em centímetros, pelo valor de cada centímetro.')],
      ],
      rationale: [
        strong('Resolução. '),
        txt(`Com a régua, a distância entre ${nP} e ${nQ} no mapa é de ${num(d)} cm.`),
        br,
        txt(
          p.tipo === 'grafica'
            ? `Na barra de escala, ${p.barraCm} cm valem ${km(p.barraCm * p.kmPorCm)}: cada centímetro do mapa vale ` +
                `${p.barraCm * p.kmPorCm} ÷ ${p.barraCm} = ${km(p.kmPorCm)}.`
            : `Na escala 1 : ${formatBR(p.kmPorCm * 100000, 0)}, 1 cm do mapa são ${formatBR(p.kmPorCm * 100000, 0)} cm na ` +
                `realidade — ${formatBR(p.kmPorCm * 100000, 0)} ÷ 100.000 = ${km(p.kmPorCm)}.`,
        ),
        br,
        txt(`Distância real: ${num(d)} × ${p.kmPorCm} = ${km(chave)}.`),
      ],
      declared: p.tipo === 'numerica' ? 'dificil' : 'media',
    };
  },
};
